import { MenuProps } from '@mui/material/Menu';
import { useCallback, useState } from 'react';


/**
 * Hook for managing the open/close state and anchor element of a Material UI menu.
 *
 * @param {HTMLElement | null} [initialAnchorEl=null] - Optional initial anchor element to control menu position.
 *
 * @returns {{
 *   openMenu: (event: React.MouseEvent<HTMLElement>) => void,
 *   closeMenu: () => void,
 *   isOpen: boolean,
 *   menuProps: Pick<MenuProps, 'anchorEl' | 'open' | 'onClose'>
 * }}
 *
 * @example
 * const { openMenu, closeMenu, isOpen, menuProps } = useMenuControl();
 * 
 * return (
 *   <>
 *     <Button onClick={openMenu}>Open Menu</Button>
 *     <Menu {...menuProps}>
 *       <MenuItem onClick={closeMenu}>Item 1</MenuItem>
 *       <MenuItem onClick={closeMenu}>Item 2</MenuItem>
 *     </Menu>
 *   </>
 * );
 */
export function useMenuControl(initialAnchorEl: HTMLElement | null = null) {
    // HTML element used to set the position of the menu
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(initialAnchorEl);

    /**
     * Event handler to open the menu.
     * Sets the anchor element to the current target of the click event,
     * which is used to position the menu.
     *
     * @param {React.MouseEvent<HTMLElement>} event - The mouse event from the element triggering the menu open.
     * @returns {void}
     */
    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);


    /**
     * Handler to close the menu.
     * Clears the anchor element to close the menu.
     *
     * @returns {void}
     */
    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    // props to pass to the menu component
    const menuProps: Pick<MenuProps, 'anchorEl' | 'open' | 'onClose'> = {
        anchorEl,
        open: Boolean(anchorEl),
        onClose: handleMenuClose,
    };

    return {
        openMenu: handleMenuOpen,
        closeMenu: handleMenuClose,
        isOpen: Boolean(anchorEl),
        menuProps,
    };
}
